import { cancelSelectEvent, getWidgetDataURI, getSymbolDropdown, getSymbolOptions, apiGet, updateSelectedSymbolInDropdown, getTraderSMCardHTML, changeSymbolForWidget, toggleWidgetTheme, getCSSStyles, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, chartWindowStart, chartWindowEnd } from "../../helpers";
import {
  CandleStickData,
  CandlestickChartConfig,
  WidgetEvent,
  WidgetEventHandler,
  WidgetEventType,
} from "../../types";
import proStyleText from "@klinecharts/pro/dist/klinecharts-pro.css?raw";
import { init, dispose, registerLocale } from "klinecharts";
import { KLineChartPro, loadLocales } from "@klinecharts/pro";
import * as LocaleJSON from "../klinechart/i18n/en-US.json";
import customStyle from "../klinechart/style.json";

const PREVIEW_PERIODS = [
  { multiplier: 1, timespan: "minute", text: "1m" },
  { multiplier: 5, timespan: "minute", text: "5m" },
  { multiplier: 15, timespan: "minute", text: "15m" },
  { multiplier: 1, timespan: "hour", text: "1H" },
  { multiplier: 2, timespan: "hour", text: "2H" },
  { multiplier: 4, timespan: "hour", text: "4H" },
  { multiplier: 1, timespan: "day", text: "D" },
  { multiplier: 1, timespan: "week", text: "W" },
  { multiplier: 1, timespan: "month", text: "M" },
  { multiplier: 1, timespan: "year", text: "Y" },
] as const;

function periodToApiParams(
  text: string,
  multiplier: number = 1
): { interval: string; sub_points: number } {
  const normalized = (text || "").trim().toLowerCase();
  const map: Record<string, { interval: string; sub_points: number }> = {
    "1m": { interval: "minute", sub_points: 1 },
    "5m": { interval: "minute", sub_points: 5 },
    "15m": { interval: "minute", sub_points: 15 },
    "1h": { interval: "hour", sub_points: 1 },
    "2h": { interval: "hour", sub_points: 2 },
    "4h": { interval: "hour", sub_points: 4 },
    "d": { interval: "daily", sub_points: 1 },
    "w": { interval: "weekly", sub_points: 1 },
    "m": { interval: "monthly", sub_points: 1 },
    "y": { interval: "yearly", sub_points: 1 },
  };
  const entry = map[normalized];
  if (entry) return entry;
  // Fallback: infer from multiplier + known timespans
  if (normalized.endsWith("m") && multiplier >= 1 && multiplier <= 60)
    return { interval: "minute", sub_points: multiplier };
  if (normalized.endsWith("h") && multiplier >= 1 && multiplier <= 24)
    return { interval: "hour", sub_points: multiplier };
  return { interval: "daily", sub_points: 1 };
}

function safeFormatDate(
  dateTimeFormat: Intl.DateTimeFormat,
  timestamp: number,
  format: string,
  _type?: number
): string {
  if (timestamp == null || !Number.isFinite(timestamp)) return "--";
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return "--";
  try {
    const dateTimeString = dateTimeFormat.format(date);
    const parts: Record<string, string> = {
      YYYY: String(date.getUTCFullYear()),
      MM: String(date.getUTCMonth() + 1).padStart(2, "0"),
      DD: String(date.getUTCDate()).padStart(2, "0"),
      HH: String(date.getUTCHours()).padStart(2, "0"),
      mm: String(date.getUTCMinutes()).padStart(2, "0"),
      ss: String(date.getUTCSeconds()).padStart(2, "0"),
    };
    return format.replace(/YYYY|MM|DD|HH|mm|ss/g, (key) => parts[key] ?? key);
  } catch {
    return "--";
  }
}

export class TraderverseKlineChartPro {
  private container: HTMLElement;
  private config: CandlestickChartConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private currentData: any[] | null = null;
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private apiHost: string;
  private isChartLoaded: boolean = false;
  private chartData: any[][] = [];
  private volumeData: number[] = [];
  private chartLabels: string[] = [];
  private currentChart: any;
  private proChart: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private defaultZoomStart: number = 98; // Show last ~2% of data (more zoomed to latest)
  private defaultZoomEnd: number = 100;
  private autoZoomToLatest: boolean = true;
  private autoZoomCandleCount: number = 30; // Show fewer candles for thicker appearance
  private readonly upColor: string = "#00FF57";
  private readonly downColor: string = "#FF0000";
  private measurementEnabled: boolean = false;
  private measurementStart: number | null = null;
  private resizeAnimationFrame: number | null = null;
  private screenshotInterceptorBound: ((e: MouseEvent) => void) | null = null;

  private maxDataPoints: number = 5000; // Limit data array size to prevent memory bloat
  private updateThrottleMs: number = 100; // Throttle chart updates to 10fps max
  private lastUpdateTime: number = 0;
  private pendingUpdate: boolean = false;
  private batchTimeout: number | null = null;

  private datafeedRequestCache: Map<string, Promise<any[]>> = new Map();
  private datafeedResultCooldownMs: number = 800;
  private datafeedResultCache: Map<string, { result: any[]; until: number }> = new Map();

  private normalizeCandleTime(raw: any): number | null {
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      const parsed = typeof raw === "string" ? new Date(raw).getTime() : NaN;
      if (!Number.isFinite(parsed)) return null;
      return parsed;
    }
    if (n > 0 && n < 1e11) return n * 1000;
    return n;
  }

  private getProDatafeed() {
    return {
      getHistoryKLineData: async (symbol: any, period: any, from: number, to: number) => {
        const mult = period?.multiplier ?? period?.subPoint ?? 1;
        const { interval, sub_points: subPoints } = periodToApiParams(
          period?.text ?? "",
          typeof mult === "number" ? mult : 1
        );
        const symbolStr = symbol?.ticker ?? symbol ?? "";
        const requestKey = `${String(symbolStr)}|${interval}|${subPoints}`;
        const nowTs = Date.now();

        const cached = this.datafeedResultCache.get(requestKey);
        if (cached && cached.until > nowTs) {
          return cached.result;
        }
        if (cached && cached.until <= nowTs) {
          this.datafeedResultCache.delete(requestKey);
        }

        const inFlight = this.datafeedRequestCache.get(requestKey);
        if (inFlight) {
          return inFlight;
        }

        const now = new Date();
        const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        const apiUrl = `${this.apiHost}/prices`;

        const requestPromise = (async () => {
          try {
            const response = await apiGet({
              url: apiUrl,
              query: {
                symbol: symbolStr,
                interval,
                sub_points: subPoints,
                start_date: chartWindowStart(oneYearAgo),
                end_date: chartWindowEnd(now),
              },
            });
            const data = await response.json();
            setTimeout(() => {
              if (this.shadowRoot) {
                const widget = this.shadowRoot.querySelector('.widgetBox');
                if (widget) widget.classList.remove('loading');
              }
            }, 0);
            const rows = (data.data || []) as any[];
            const result: any[] = [];
            for (const item of rows) {
              const timeMs = this.normalizeCandleTime(item.timestamp);
              if (timeMs == null) continue;
              result.push({
                timestamp: timeMs,
                open: Number(item.open) || 0,
                high: Number(item.high) || 0,
                low: Number(item.low) || 0,
                close: Number(item.close) || 0,
                volume: Number(item.volume) || 0,
              });
            }
            // KLineChart expects oldest-first; API may return newest-first.
            result.sort((a, b) => a.timestamp - b.timestamp);
            if (result.length === 0) {
              const chartContainer = this.shadowRoot.querySelector('#tv_container') as HTMLElement | null;
              if (chartContainer) chartContainer.innerHTML = getNoDataHTML();
            }
            this.datafeedResultCache.set(requestKey, {
              result,
              until: Date.now() + this.datafeedResultCooldownMs,
            });
            return result;
          } catch (e) {
            return [];
          } finally {
            this.datafeedRequestCache.delete(requestKey);
          }
        })();

        this.datafeedRequestCache.set(requestKey, requestPromise);
        return requestPromise;
      },
      searchSymbols: async () => [],
      subscribe: async () => { },
      unsubscribe: async () => { },
    };
  }

  private trimDataArrays(): void {
    if (!this.currentData || this.currentData.length <= this.maxDataPoints) {
      return;
    }

    const excessCount = this.currentData.length - this.maxDataPoints;

    this.currentData.splice(0, excessCount);
    this.chartLabels.splice(0, excessCount);
    this.chartData.splice(0, excessCount);
    this.volumeData.splice(0, excessCount);
  }

  private scheduleChartUpdate(): void {
    const now = Date.now();

    if (now - this.lastUpdateTime >= this.updateThrottleMs) {
      this.performChartUpdate();
      this.lastUpdateTime = now;
      return;
    }

    if (!this.pendingUpdate) {
      this.pendingUpdate = true;
      const remainingTime = this.updateThrottleMs - (now - this.lastUpdateTime);

      setTimeout(() => {
        this.performChartUpdate();
        this.lastUpdateTime = Date.now();
        this.pendingUpdate = false;
      }, remainingTime);
    }
  }

  private performChartUpdate(): void {
    if (!this.currentChart || !this.isChartLoaded || !this.currentData) return;
    try {
      const klineData = this.currentData.map((item) => ({
        timestamp: item.time * 1000,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
        volume: item.volume || 0,
      }));

      this.currentChart.applyNewData(klineData);
      this.emit("dataUpdate", this.currentData);
    } catch (error) {
      console.error("Error updating chart:", error);
    }
  }

  constructor(config: CandlestickChartConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.apiHost = this.config.apiHost ?? "https://facts.traderverse.io/chartapi";
    this.container = this.resolveContainer(config.container);
    this.measurementEnabled = this.isAdminMeasurementEnabled();
    if (this.measurementEnabled) {
      this.measurementStart = this.getNow();
    }
    // Ensure widgetBox is always inside a shadowRoot: use host's shadowRoot or attach one.
    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: "open" });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
      if (widget) {
        this.widgetResizeObserver?.observe(widget);
      }
    }, 0);
  }

  private getDefaultConfig(): Partial<CandlestickChartConfig> {
    const symbols = getSymbolOptions();
    return {
      headerPinned: true,
      symbol: symbols[0]?.stockId ?? "AAPL_NASDAQ",
      theme: "default-dark",
      width: "100%",
      interval: "daily",
      period: "daily",
      height: "400px",
      autosize: true,
    };
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
      if (typeof proStyleText === "string" && proStyleText.length > 0) {
        const proEl = document.createElement("style");
        proEl.textContent = proStyleText;
        this.shadowRoot.appendChild(proEl);
      }
      const chartOverrides = document.createElement("style");
      chartOverrides.textContent = `
        .widgetBox .inBody .chartDv { overflow: hidden !important; display: flex !important; flex-direction: column !important; }
        #tv_container { display: flex !important; flex-direction: column !important; min-height: 0 !important; flex: 1 !important; pointer-events: auto !important; }
        #tv_container.klinecharts-pro { height: 100% !important; min-height: 0 !important; flex: 1 !important; position: relative !important; }
        .klinecharts-pro-period-bar { position: relative !important; z-index: 20 !important; flex-shrink: 0 !important; pointer-events: auto !important; }
        .klinecharts-pro-period-bar .symbol { display: none !important; pointer-events: none !important; }
        .klinecharts-pro-period-bar *, .klinecharts-pro-period-bar .item, .klinecharts-pro-period-bar .tools, .klinecharts-pro-period-bar .menu-container { pointer-events: auto !important; cursor: pointer !important; }
        .klinecharts-pro-content { position: relative !important; z-index: 1 !important; }
        .klinecharts-pro-drawing-bar, .klinecharts-pro-modal, .klinecharts-pro-modal * { pointer-events: auto !important; z-index: 99 !important; }
      `;
      this.shadowRoot.appendChild(chartOverrides);
    } catch (err) {
      console.error("Failed to load CSS:", err);
    }
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === "string") {
      const element =
        document.getElementById(container) || document.querySelector(container);
      if (!element) {
        console.error(`Container not found: ${container}`);
        throw new Error(`Container not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) {
      this.config.symbol = getSymbolOptions()[0].stockId;
    } else if (!this.config.symbol.includes("_")) {
      this.config.symbol = `${this.config.symbol}_NASDAQ`;
    }
    this.createWidget();
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    if (widget) widget.classList.add("loading");
    this.attachStyles();
    this.setupEventListeners();
    await this.injectChartLibrary();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove("loading");
    this.emit("ready", {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let widgetInstanceCounter = 0;
    const widgetIndex = widgetInstanceCounter++;     
    let themeClass = "defaultDarkMode";
    let dataTheme = "default-dark";
    if (this.config.theme === "white") {
      themeClass = "whiteMode";
      dataTheme = "white";
    } else if (this.config.theme === "black") {
      themeClass = "blackMode";
      dataTheme = "black";
    } else if (this.config.theme === "default-lite") {
  themeClass = "defaultLightMode";
  dataTheme = "default-lite";
}
    const widgetHtml = `
    <div  data-testing-id="candlestickchartpro-performance-widget-${widgetIndex}" style="width: ${typeof this.config.width === "number" ? this.config.width + "px" : this.config.width}; height: ${typeof this.config.height === "number" ? this.config.height + "px" : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
      <div class="headTop">
        <h4 class="title stock">${this.getTitleIconSvg()} Candlestick Chart</h4>
        <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
      </div>
      <div class="inBody">
        <div class="optionsRow">
          ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
            <div class="trader-sm-card" id="selected-symbol">${getTraderSMCardHTML(
      this.config.stock ?? {
        symbol: this.config.symbol!.split("_")[0],
        type: "stock",
        name: "",
      }
    )}</div>
            <span id="symbol-dropdown-placeholder"></span>
          </div>`}
        </div>
        <div class="chartDv" style="min-height: 360px;">
          <div class="chart coverDv" id="tv_container" style="width: 100%; height: 100%; min-height: 320px;"></div>
        </div>
      </div>
    </div>
    `;

    const chartHtml = widgetHtml;

    this.shadowRoot.innerHTML = chartHtml;

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) =>
        this.onSelectChange(data)
      );
      const placeholder = this.shadowRoot.querySelector(
        "#symbol-dropdown-placeholder"
      );
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }


  onSelectChange(stock: any) {
    this.changeSymbol(stock.stockId, stock);
  }


  
  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
  }

  private async injectChartLibrary(): Promise<boolean> {
    return Promise.resolve(true);
  }

  private requestStocksDataTimeout: number | null = null;


  private async setupChart(stockId: string): Promise<void> {
    this.isChartLoaded = false;
    if (this.proChart) {
      try {
        if (typeof this.proChart.dispose === "function") {
          this.proChart.dispose();
        }
      } catch (e) {
        console.warn("Error disposing previous chart:", e);
      }
      this.proChart = null;
    }
    const container = this.shadowRoot.querySelector("#tv_container");
    if (!container) {
      console.error("Chart container #tv_container not found or not visible.");
      return;
    }
    container.innerHTML = "";
    if (typeof registerLocale === "function") registerLocale("en-US", LocaleJSON as any);
    if (KLineChartPro && loadLocales) loadLocales("en-US", LocaleJSON);
    const intervalKey = (this.config.interval || "daily").replace(/^(\d)([mhDWMY])$/i, (_: string, n: string, u: string) => n + u.toUpperCase());
    const initialPeriod = PREVIEW_PERIODS.find((p) => p.text === intervalKey || p.text.toLowerCase() === (this.config.interval || "daily").toLowerCase() || (intervalKey === "daily" && p.text === "D")) ?? PREVIEW_PERIODS[6]; // daily default -> use D period; fallback to D (index 6)
    const symbolInfo = { ticker: stockId };
    const containerEl = container as HTMLElement;
    containerEl.style.width = "100%";
    containerEl.style.height = "100%";
    containerEl.style.minHeight = "320px";
    this.proChart = new KLineChartPro({
      container: containerEl,
      theme: this.config.theme === "black" ? "dark" : "light",
      locale: "en-US",
      periods: [...PREVIEW_PERIODS],
      drawingBarVisible: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC",
      period: initialPeriod,
      styles: customStyle as any,
      watermark: "",
      symbol: symbolInfo,
      mainIndicators: [],
      subIndicators: [],
      datafeed: this.getProDatafeed(),
    });
    this.applySafeFormatDateAndChartRef();
    this.attachScreenshotInterceptor(containerEl);
    this.isChartLoaded = true;
    this.emit("ready", {});
    // Trigger resize so toolbar and drawing bar layout; retry after data may have loaded
    setTimeout(() => this.handleResize(), 150);
    setTimeout(() => this.handleResize(), 600);
    setTimeout(() => this.handleResize(), 1200);
  }

  /** Intercept toolbar screenshot click and use widget capture to avoid chart's 0-sized canvas error. */
  private attachScreenshotInterceptor(containerEl: HTMLElement): void {
    if (this.screenshotInterceptorBound) {
      containerEl.removeEventListener("click", this.screenshotInterceptorBound, true);
    }
    this.screenshotInterceptorBound = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest) return;
      const periodBar = target.closest(".klinecharts-pro-period-bar");
      if (!periodBar) return;
      const screenshotLabels = ["Screenshot", "截屏"]; // en-US, zh-CN
      let el: HTMLElement | null = target;
      while (el && el !== periodBar) {
        const t = el.textContent?.trim();
        if (t && screenshotLabels.includes(t)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleScreenshotClick();
          return;
        }
        el = el.parentElement;
      }
    };
    containerEl.addEventListener("click", this.screenshotInterceptorBound, true);
  }

  private handleScreenshotClick(): void {
    this.getWidgetImage().then((dataUrl) => {
      if (!dataUrl) return;
      const a = document.createElement("a");
      a.download = "screenshot.png";
      a.href = dataUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
  }

  private applySafeFormatDateAndChartRef(): void {
    const apply = (): void => {
      const chartApi = (this.proChart as any)?._chartApi;
      if (!chartApi) return;
      if (typeof chartApi.setCustomApi === "function") {
        chartApi.setCustomApi({ formatDate: safeFormatDate });
      }
      // Ensure resize() runs before getConvertPictureUrl so no pane has a 0-sized canvas (fixes screenshot InvalidStateError).
      const origGetConvertPictureUrl = chartApi.getConvertPictureUrl;
      if (typeof origGetConvertPictureUrl === "function") {
        chartApi.getConvertPictureUrl = (
          includeOverlay?: boolean,
          type?: string,
          backgroundColor?: string
        ): string => {
          if (typeof chartApi.resize === "function") {
            try {
              chartApi.resize();
            } catch {
              // ignore resize errors
            }
          }
          return origGetConvertPictureUrl.call(
            chartApi,
            includeOverlay,
            type,
            backgroundColor
          );
        };
      }
      this.currentChart = chartApi;
    };
    apply();
    setTimeout(apply, 0);
    setTimeout(apply, 100);
    setTimeout(apply, 300);
  }

  private handleResize(): void {
    if (this.resizeAnimationFrame) {
      cancelAnimationFrame(this.resizeAnimationFrame);
    }

    this.resizeAnimationFrame = requestAnimationFrame(() => {
      this.resizeAnimationFrame = null;
      if (this.currentChart && typeof (this.currentChart as any).resize === "function") {
        try {
          (this.currentChart as any).resize();
          return;
        } catch (e) {
        }
      }

      if (this.currentData) {
        this.updateDisplay();
      }
    });
  }

  private updateDisplay(): void {
    if (!this.currentData) return;
    this.isChartLoaded = false;
    if (this.measurementEnabled) {
      this.measurementStart = this.getNow();
    }
  }


  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (stockId) => this.setupChart(stockId),
      (type, data) => this.emit(type, data)
    );
  }

  private toggleTheme(): void {
    const widget = this.shadowRoot.querySelector(".traderverse-widget");
    const themeToggle = this.shadowRoot.querySelector(".theme-toggle");
    if (widget) {
      const currentTheme = widget.getAttribute("data-theme");
      const newTheme = currentTheme === "dark" ? "light" : "dark";
      widget.setAttribute("data-theme", newTheme);
      this.config.theme = newTheme as "white" | "black";

      if (themeToggle) {
        themeToggle.textContent = newTheme === "dark" ? "☀️" : "🌙";
      }
      this.updateDisplay();
    }
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.container.requestFullscreen?.();
    }
  }

  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public setSymbol(stockId: string): void {
    this.changeSymbol(stockId);
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }

  public getCurrentData(): CandleStickData[] | null {
    return this.currentData;
  }

  public toggleSeries(seriesName: string, visible: boolean): void {
  }

  public addCustomSeries(seriesConfig: any): void {
  }

  public zoomToRange(startPercent: number, endPercent: number): void {
    if (!this.currentChart || !this.currentData) return;

    const totalCandles = this.currentData.length;
    const from = Math.floor((startPercent / 100) * totalCandles);
    const to = Math.ceil((endPercent / 100) * totalCandles);
    this.currentChart.zoomAtDataIndex(from, to);
  }

  public resetZoom(): void {
    if (!this.currentChart || !this.currentData) return;
    this.currentChart.zoomAtDataIndex(0, this.currentData.length);
  }

  public enableZoom(enabled: boolean): void {
  }

  public simulateTradeUpdate(tradeData: any): void {
  }

  public getChartState(): any {
    return {
      isChartLoaded: this.isChartLoaded,
      currentDataLength: this.currentData?.length || 0,
      chartDataLength: this.chartData.length,
      volumeDataLength: this.volumeData.length,
      currentSymbol: this.currentData?.[0]?.symbol || "unknown",
    };
  }

  public setDefaultZoom(startPercent: number, endPercent: number): void {
    this.defaultZoomStart = Math.max(0, Math.min(100, startPercent));
    this.defaultZoomEnd = Math.max(0, Math.min(100, endPercent));
    this.zoomToRange(startPercent, endPercent);
  }

  public setAutoZoomToLatest(enabled: boolean, candleCount: number = 50): void {
    this.autoZoomToLatest = enabled;
    this.autoZoomCandleCount = candleCount;
  }

  public setMaxDataPoints(maxPoints: number): void {
    this.maxDataPoints = Math.max(100, maxPoints); // Minimum 100 points
    this.trimDataArrays(); // Apply limit immediately
  }

  public setCandlestickStyle(
    upColor: string = this.upColor,
    downColor: string = this.downColor,
    thickness: "thin" | "normal" | "thick" = "thick"
  ): void {
    if (!this.currentChart) return;

    this.currentChart.setStyles({
      candle: {
        type: "candle_solid",
        bar: {
          upColor,
          downColor,
          upBorderColor: upColor,
          downBorderColor: downColor,
          upWickColor: upColor,
          downWickColor: downColor,
        },
      },
    });
  }

  public zoomToRecent(minutes: number = 60): void {
    this.zoomToLatest(Math.min(minutes, this.currentData?.length || 0));
  }

  public zoomToLatest(candleCount: number = 50): void {
    if (
      !this.currentChart ||
      !this.currentData ||
      this.currentData.length === 0
    )
      return;

    const totalCandles = this.currentData.length;
    const from = Math.max(0, totalCandles - candleCount);
    this.currentChart.zoomAtDataIndex(from, totalCandles);
  }

  private adjustBarWidth(visibleCandles: number): void {
    // Auto-handled by klinecharts
  }

  public zoomPresets = {
    latest25: () => this.zoomToLatest(25), // Show last 25 candles
    latest50: () => this.zoomToLatest(50), // Show last 50 candles
    latest100: () => this.zoomToLatest(100), // Show last 100 candles
    recent30min: () => this.zoomToRecent(30),
    recent1hour: () => this.zoomToRecent(60),
    recent2hours: () => this.zoomToRecent(120),
    recent4hours: () => this.zoomToRecent(240),
    showAll: () => this.resetZoom(),
  };

  public getTimezoneInfo(): any {
    const now = new Date();
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: now.getTimezoneOffset(),
      localTime: now.toLocaleString(),
      utcTime: now.toISOString(),
      offsetHours: Math.floor(Math.abs(now.getTimezoneOffset()) / 60),
      offsetMinutes: Math.abs(now.getTimezoneOffset()) % 60,
      offsetDirection: now.getTimezoneOffset() > 0 ? "behind" : "ahead",
    };
  }

  private areDataArraysConsistent(): boolean {
    if (!this.currentData) return true;

    const dataLength = this.currentData.length;
    return (
      this.chartData.length === dataLength &&
      this.volumeData.length === dataLength &&
      this.chartLabels.length === dataLength
    );
  }

  public destroy(): void {
    // Clear all timeouts and intervals
    if (this.updateInterval) {
      window.clearInterval(this.updateInterval);
      this.updateInterval = null;
      if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
      this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';

    }


    if (this.requestStocksDataTimeout) {
      clearTimeout(this.requestStocksDataTimeout);
      this.requestStocksDataTimeout = null;
    }

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    this.datafeedRequestCache.clear();
    this.datafeedResultCache.clear();

    try {
      const win = window as any;
      if (win.__TraderverseKlineChartProInstances) {
        win.__TraderverseKlineChartProInstances.delete(this);
        if (
          win.__TraderverseKlineChartProInstances.size === 0 &&
          win.__TraderverseKlineChartProSocketHandler &&
          win.socket
        ) {
          try {
            win.socket.off(
              "tradeupdate",
              win.__TraderverseKlineChartProSocketHandler
            );
          } catch (e) {
          }
          win.__TraderverseKlineChartProSocketRegistered = false;
          win.__TraderverseKlineChartProSocketHandler = null;
        }
      }
    } catch (e) {
    }

    if (this.proChart) {
      try {
        this.proChart.dispose();
      } catch (e) {
        console.warn("Error disposing chart:", e);
      }
      this.proChart = null;
      this.currentChart = null;
    }

    this.currentData = null;
    this.chartData = [];
    this.volumeData = [];
    this.chartLabels = [];

    this.eventHandlers.clear();

    this.shadowRoot.innerHTML = "";

    try {
      const win = window as any;
      if (win.__TraderverseStockPollers) {
        for (const sym in win.__TraderverseStockPollers) {
          const p = win.__TraderverseStockPollers[sym];
          if (p && p.instances) {
            p.instances.delete(this);
            if (p.instances.size === 0) {
              if (p.intervalId) {
                clearInterval(p.intervalId);
              }
              delete win.__TraderverseStockPollers[sym];
            }
          }
        }
      }
    } catch (e) {
    }
  }

  private formatLocalTime(date: Date): string {
    if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
      return "";
    }
    const interval = this.config.interval || "daily";
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // For intraday intervals (minutes/hours), show time
    if (interval.includes("m") || interval.includes("h")) {
      // For very short intervals (1m, 5m), show only time
      if (interval === "1m" || interval === "5m" || interval === "15m") {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: false,
          timeZone: tz,
        });
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: false,
        timeZone: tz,
      });
    }

    return date.toLocaleDateString("en-US", {
      year: "2-digit",
      month: "short",
      day: "numeric",
      timeZone: tz,
    });
  }

  private findInsertIndex(newTime: Date): number {
    if (!this.currentData || this.currentData.length === 0) return 0;

    const newTimeMs = newTime.getTime();
    for (let i = this.currentData.length - 1; i >= 0; i--) {
      if (this.currentData[i].time * 1000 <= newTimeMs) {
        return i + 1;
      }
    }
    return 0;
  }

  private isValidTimestamp(timestamp: number): boolean {
    const now = Date.now();
    const oneYearAgo = now - 365 * 24 * 60 * 60 * 1000;
    const oneYearFromNow = now + 365 * 24 * 60 * 60 * 1000;

    return timestamp >= oneYearAgo && timestamp <= oneYearFromNow;
  }

  private shouldCreateNewCandle(newTime: Date): {
    shouldCreate: boolean;
    updateIndex: number;
  } {
    if (!this.currentData || this.currentData.length === 0) {
      return { shouldCreate: true, updateIndex: -1 };
    }

    const lastCandle = this.currentData[this.currentData.length - 1];
    const lastCandleTime = new Date(lastCandle.time * 1000);

    const isSameMinute = lastCandleTime.getTime() === newTime.getTime();

    if (isSameMinute) {
      return { shouldCreate: false, updateIndex: this.currentData.length - 1 };
    }

    if (newTime.getTime() > lastCandleTime.getTime()) {
      return { shouldCreate: true, updateIndex: -1 };
    }

    for (let i = this.currentData.length - 1; i >= 0; i--) {
      const candleTime = new Date(this.currentData[i].time * 1000);
      if (candleTime.getTime() === newTime.getTime()) {
        return { shouldCreate: false, updateIndex: i };
      }
    }

    return { shouldCreate: true, updateIndex: -1 };
  }

  private getNow(): number {
    return typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();
  }

  private isAdminMeasurementEnabled(): boolean {
    try {
      if (typeof localStorage === "undefined") return false;
      const flag = localStorage.getItem("admin");
      const perfFlag = localStorage.getItem("admin_measure_ui");
      return flag === "true" || perfFlag === "true";
    } catch (e) {
      return false;
    }
  }

  private logUILoadTime(event: string): void {
    if (!this.measurementEnabled || this.measurementStart === null) return;
    const duration = this.getNow() - this.measurementStart;
    const mem = this.getMemoryInfo();
    const memSuffix = mem
      ? ` heap_used_mb=${mem.usedMB} heap_total_mb=${mem.totalMB}`
      : "";
    console.info(
      `[TraderverseKlineChartPro][perf][${event}] ui_load_ms=${duration.toFixed(1)}${memSuffix}`
    );
    this.measurementStart = null;
  }

  private getMemoryInfo(): { usedMB: string; totalMB: string } | null {
    try {
      const perf: any = (window as any).performance;
      if (!perf || !perf.memory) return null;
      const { usedJSHeapSize, totalJSHeapSize } = perf.memory;
      return {
        usedMB: (usedJSHeapSize / 1024 / 1024).toFixed(2),
        totalMB: (totalJSHeapSize / 1024 / 1024).toFixed(2),
      };
    } catch (e) {
      return null;
    }
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const event: WidgetEvent = { type, data };
      handlers.forEach((handler) => handler(event));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window {
    TraderverseKlineChartPro: typeof TraderverseKlineChartPro;
  }
}

if (typeof window !== "undefined") {
  window.TraderverseKlineChartPro = TraderverseKlineChartPro;
}
